import { useState, useEffect } from 'react';
import { Check, Play } from 'lucide-react';
import { Button } from '@/components/shadcn-ui/button';
import { cn } from '@/lib/utils';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';


const PriceDisplay = ({ price, billing, isEnterprise }) => {
  if (isEnterprise || typeof price === 'string') {
    return (
      <div className="flex items-center justify-center h-[72px]">
        <span className="text-[28px] font-bold text-[#c9a84c] leading-tight text-center">{price}</span>
      </div>
    );
  }

  const integer = Math.floor(price);
  const cents = String(price.toFixed(2)).split('.')[1];

  return (
    <div className="flex items-start justify-center gap-0.5 h-[72px]">
      <span className="text-xl font-semibold text-white mt-1 leading-none">$</span>
      <span className="text-[56px] font-bold text-white leading-none">{integer}</span>
      <span className="text-xl font-semibold text-white mt-1 leading-none">{cents}</span>
    </div>
  );
};

const PlanCard = ({ planKey, plan, isHighlighted, isEnterprise, onCheckout, isCheckingOut }) => {
  const accentColor = isEnterprise ? 'text-[#c9a84c]' : isHighlighted ? 'text-[#3b82f6]' : 'text-white';
  const borderClass = isEnterprise
    ? 'border-[#c9a84c]'
    : isHighlighted
      ? 'border-[#2563eb]'
      : 'border-[#2a2d35]';

  const billingLabel = plan.billing === 'per_permit' ? '/per permit' : plan.billing === 'monthly' ? '/monthly' : null;
  const navigate = useNavigate();
  const handleNewPermit = () => {
    navigate('/citypermit/project/new-chat');
  };

  return (
    <div className={cn('flex flex-col rounded-xl border bg-[#111318] p-6', borderClass)}>
      {/* Name */}
      <h3 className={cn('text-base font-semibold text-center mb-4', accentColor)}>{plan.name}</h3>

      {/* Price */}
      <PriceDisplay price={plan.price} billing={plan.billing} isEnterprise={isEnterprise} />

      {/* Billing label */}
      <p className="text-center text-sm text-gray-400 mt-1 mb-4 min-h-[20px]">{billingLabel ?? ''}</p>

      {/* Description */}
      <p className="text-center text-xs text-gray-400 leading-relaxed mb-5 min-h-[60px]">{plan.description}</p>

      {/* Divider */}
      <div className="border-t border-[#2a2d35] mb-4" />

      {/* What's Included */}
      <p className={cn('text-sm font-semibold mb-3', accentColor)}>What&apos;s Included</p>

      <ul className="space-y-2.5 flex-1 min-h-[160px]">
        {plan.features.map((feature, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
            <Check className={cn('mt-0.5 shrink-0 w-4 h-4', accentColor)} />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <div className="mt-6">
        {isEnterprise ? (
          <>
            <Button
              className="w-full bg-[#c9a84c] hover:bg-[#b8963a] text-black font-semibold rounded-md h-10 flex items-center justify-center gap-2"
              onClick={() => toast.info('Redirecting to enterprise inquiry...')}
            >
              Get a Quote
              <span className="inline-flex items-center justify-end w-5 h-5 rounded-sm">
                <Play className="w-3 h-3 fill-current text-white" />
              </span>
            </Button>
            <p className="text-center text-[11px] text-gray-500 mt-2">Flexible contract options</p>
          </>
        ) : planKey === 'starter' ? (
          <>
            <Button
              variant="outline"
              className="w-full border-[#3a3d46] bg-transparent text-white hover:bg-[#1e2028] hover:text-white/90 rounded-md h-10 text-sm"
              onClick={() => handleNewPermit()}
            >
              Chat With Zermit Now
            </Button>
            <p className="text-center text-[11px] text-gray-500 mt-2 invisible">
              Cancel Anytime. No long-term contracts.
            </p>
          </>

        ) : (
          <>
            <Button
              className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold rounded-md h-10 text-sm disabled:opacity-60"
              disabled={isCheckingOut}
              onClick={onCheckout}
            >
              {isCheckingOut ? 'Redirecting...' : `Start with ${plan.name}`}
            </Button>
            <p className="text-center text-[11px] text-gray-500 mt-2">Cancel Anytime. No long-term contracts.</p>
          </>
        )}
      </div>
    </div>
  );
};

const SubscriptionPage = ({ protectedRoute = true }) => {
  const [plans, setPlans] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [checkingOutKey, setCheckingOutKey] = useState(null);
  const navigate = useNavigate();
  const handleCheckout = async (planKey, priceId) => {
    if (!protectedRoute) {
      navigate('/citypermit/subscription');
      return
    }
    setCheckingOutKey(planKey);
    try {
      // console.log(planKey, priceId);
      const { data } = await appAxios.post('/Subscription/v1.0/create-checkout-session', { price_id: priceId, planName: planKey });
      window.location.href = data.data.checkout_url;
    } catch (error) {
      if (error.response.data.ui_action === "already_active_subscription_toast") {
        toast.info('You are already subscribed user.');
        return;
      }
      if (error.response.data.ui_action === "show_wait_toast") {
        toast.info('We are setting up your account. Please wait for a moment.');
        return;
      }

      console.error('Checkout failed:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCheckingOutKey(null);
    }
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        let url
        if (protectedRoute) {
          url = '/Subscription/v1.0/plans';
        } else {
          url = '/PermitChat/v1.0/plans';
        }
        const { data } = await appAxios.get(url);
        setPlans(data.data);
      } catch (error) {
        console.error('Failed to fetch subscription plans:', error);
        toast.error('Failed to load subscription plans.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const planOrder = ['starter', 'pro_1', 'pro_2', 'enterprise'];
  const professionalKeys = ['pro_1', 'pro_2'];

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto no-scrollbar">
      <div className="min-h-screen bg-[#0a0a0a]">
        {/* Hero Header */}
        <div className="w-full bg-[#0a0a0a] py-14 px-6 text-center">
          <h1 className="text-4xl font-bold text-white mb-4 leading-tight">
            Permit Automation Plans for Professionals
          </h1>
          <p className="text-gray-400 text-sm max-w-md mx-auto leading-relaxed">
            Pull over-the-counter (OTC) and like-to-like permits online through AI-powered permit automation. Choose a
            monthly plan or pay per permit no long-term contracts required.
          </p>
        </div>

        {/* Plans Section */}
        <div className="bg-[#0e1014] py-10 px-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-7 h-7 border-2 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : plans ? (
            <div className="m-2 mx-auto max-w-7xl">
              {/* Professional Plans bracket */}
              <div className="grid grid-cols-4 mb-4">
                {/* empty column above Pay As You Go */}
                <div />

                {/* bracket spanning Pro 1.0 → Enterprise (3 columns) */}
                <div className="col-span-3 flex-col items-center mb-6 px-8 hidden md:flex">
                  <span className="text-[11px] text-gray-500 tracking-widest mb-2">Professional Plans</span>
                  <div className="flex items-start w-full">
                    <div className="w-px h-3 bg-[#2a2d35]" />
                    <div className="flex-1 h-px bg-[#2a2d35]" />
                    <div className="w-px h-3 bg-[#2a2d35]" />
                  </div>
                </div>
              </div>

              {/* Plan Cards */}
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 ">
                {planOrder.map(key => {
                  const plan = plans[key];
                  if (!plan) return null;
                  return (
                    <PlanCard
                      key={key}
                      planKey={key}
                      plan={plan}
                      isHighlighted={professionalKeys.includes(key)}
                      isEnterprise={key === 'enterprise'}
                      onCheckout={() => handleCheckout(key, plan.price_id)}
                      isCheckingOut={checkingOutKey === key}
                    />
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-400 py-20">No plans available at this time.</p>
          )}
        </div>
      </div>
    </div >
  );
};

export default SubscriptionPage;
